import type { Bundle, Reference, Resource } from '@medplum/fhirtypes'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { getReferenceString } from '#src/references.ts'

// Thrown when the current version cannot be located in the supplied history
// bundle — e.g. `readHistory` returned a stale or truncated page. Callers
// should treat this as "I cannot determine the previous version" and decide
// defensively (skip vs. retry) rather than silently comparing against the
// wrong version.
export class CurrentVersionNotInHistoryError extends Error {
  constructor(versionId: string | undefined) {
    super(
      `Current version ${versionId ?? '(unknown)'} not found in history bundle`
    )
    this.name = 'CurrentVersionNotInHistoryError'
  }
}

export function getPreviousVersionFromHistory<T extends Resource>(
  history: Bundle<T>,
  current: T | ExtendedResource<T>
): T | undefined {
  const versionId =
    current instanceof ExtendedResource
      ? current.versionId
      : current.meta?.versionId

  const entries = history.entry
  invariant(entries, 'Bundle history must have entries to get previous version')
  const index = entries.findIndex(
    (entry) => entry.resource?.meta?.versionId === versionId
  )
  // `findIndex` returning -1 means the current version isn't in the page we
  // were given — usually a stale/truncated history read. Don't silently fall
  // through to `entries[0]`, which would compare against an unrelated version.
  if (index === -1) {
    throw new CurrentVersionNotInHistoryError(versionId)
  }
  if (index >= entries.length - 1) {
    return undefined
  }
  return entries[index + 1].resource
}

export function generateMedplumAppLink<
  T extends Resource | Reference | ExtendedResource,
>(resource: T, pathSuffix = ''): string {
  const reference =
    resource instanceof ExtendedResource
      ? resource.reference.reference
      : getReferenceString(resource)

  return `https://app.medplum.com/${reference}${pathSuffix}`
}

/**
 * Clones a resource, invoking the constructor of the original object.
 *
 * This method must be used to clone resources as opposed to @medplum/core#deepClone (https://www.medplum.com/docs/sdk/core.deepclone)
 * or lodash#cloneDeep https://lodash.com/docs/4.17.15#cloneDeep as they do not invoke the inputs constructor.
 * Invoking the constructor is necessary to setup the custom extensions and computed properties
 *
 * @param value - The resource to clone
 * @returns A deep clone of the input resource
 */
export function cloneResource<T>(value: T): T
export function cloneResource(value: null | undefined): undefined
export function cloneResource<T>(value: T | null | undefined): T | undefined
export function cloneResource<T>(value: T | null | undefined): T | undefined {
  if (!value) {
    return
  }

  return new (value as any).constructor(JSON.parse(JSON.stringify(value)))
}
